import {gql} from '@apollo/client';
import {partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import OrganizationDAO from 'orgs/OrganizationDAO';
import {Header3, Sublabel} from 'ui-library/Headers';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {
  InstitutionProjectBackupRecordsQuery_currentUser_UserNode_institutionProjectBackupRecords_InstitutionProjectBackupRecordNode as BackupRecord,
  InstitutionProjectBackupRecordsQuery,
} from './__generated/InstitutionProjectBackupRecordsQuery';

export const BACKUP_RECORDS_QUERY = gql`
  query InstitutionProjectBackupRecordsQuery @noReadOnly @skipAudit @engComponent(name: IDENTITY) {
    currentUser {
      ... on UserNode {
        id
        institution {
          id
        }
        institutionProjectBackupRecords {
          project {
            id
            name
            ownerJson
            url
          }
          institution {
            id
            fullName
          }
        }
      }
    }
  }
`;

interface BackupRecordsTableProps {
  backupRecords: Array<BackupRecord>;
  isUserMember: boolean;
}
function BackupRecordsTable(props: BackupRecordsTableProps): JSX.Element {
  return (
    <Table numRows={props.backupRecords.length}>
      <Column
        header={
          <Cell>
            <Header3>{props.backupRecords[0].institution.fullName}</Header3>
            {props.isUserMember && (
              <Sublabel className={css(styles.institutionHeader)}>
                You are a member of this institution
              </Sublabel>
            )}
            Project
          </Cell>
        }
        renderCell={(rowIndex: number) => {
          const project = props.backupRecords[rowIndex].project;
          return (
            <Cell>
              <a target="_blank" rel="noopener noreferrer" href={project.url}>
                {project.name}
              </a>
            </Cell>
          );
        }}
      />
      <Column
        header={<Cell className={css(styles.ownerCell)}>Owner</Cell>}
        renderCell={(rowIndex: number) => {
          const ownerJson = props.backupRecords[rowIndex].project.ownerJson;
          if (ownerJson.type === 'organization') {
            return (
              <Cell>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={OrganizationDAO.organizationURL(ownerJson.url_identifier)}
                >
                  {ownerJson.name}
                </a>
              </Cell>
            );
          } else {
            return <Cell>{ownerJson.name}</Cell>;
          }
        }}
      />
    </Table>
  );
}

export default function BackupRecordsTables(): JSX.Element {
  return (
    <BenchlingQuery<InstitutionProjectBackupRecordsQuery>
      query={BACKUP_RECORDS_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      fetchPolicy="network-only"
    >
      {({currentUser}, {refetch}) => {
        assert(currentUser?.__typename === 'UserNode', `Non-user should not access this page.`);
        if (!currentUser) {
          return null;
        }
        if (!currentUser.institutionProjectBackupRecords.length) {
          return <strong>You currently have no projects backed up to an institution.</strong>;
        }
        const projectBackupRecordsByInstitution = _.groupBy(
          currentUser.institutionProjectBackupRecords,
          (backupRecord) => backupRecord.institution.id
        );
        const [memberInstitutionIds, nonMemberInstitutionIds] = partition(
          Object.keys(projectBackupRecordsByInstitution),
          (institutionId) => currentUser.institution?.id === institutionId
        );
        return (
          <>
            {[...memberInstitutionIds, ...nonMemberInstitutionIds].map((institutionId) => (
              <BackupRecordsTable
                key={institutionId}
                isUserMember={Boolean(currentUser.institution?.id === institutionId)}
                backupRecords={projectBackupRecordsByInstitution[institutionId]}
              />
            ))}
          </>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  ownerCell: {
    verticalAlign: 'bottom',
  },
  institutionHeader: {
    marginBottom: 10,
  },
});
